// profileMatching.js
// Continuous profile-matching: student's facet scores -> distance to each
// profile's centroid -> best + runner-up profile, per facet group.
//
// Designed to plug into the existing FutureReady radar chart data with no
// changes to how that data is shaped: pass the same `labels` and
// `datasets[0].data` arrays that already feed the Radar component.

const PROFILE_GROUPS_BASE = '/api/questions/profile-groups';

// Always-on in-memory cache for the lifetime of the page — even callers
// that don't wire up persistent caching (see loadProfileGroupByName below)
// still avoid refetching the same group repeatedly within one visit.
const sessionCache = new Map();

async function fetchJson(url, headers) {
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Request failed: ${url} (${res.status})`);
  return res.json();
}

async function fetchProfileGroupFromNetwork(groupName, headers) {
  const groups = await fetchJson(PROFILE_GROUPS_BASE, headers);
  const group = groups.find((g) => g.name === groupName && g.is_active);
  if (!group) return null;

  const [facets, profiles] = await Promise.all([
    fetchJson(`${PROFILE_GROUPS_BASE}/${group.id}/facets`, headers),
    fetchJson(`${PROFILE_GROUPS_BASE}/${group.id}/profiles`, headers),
  ]);

  return {
    group,
    facets: [...facets].sort((a, b) => a.display_order - b.display_order),
    profiles: profiles
      .filter((p) => p.is_active)
      .sort((a, b) => a.display_order - b.display_order),
  };
}

// Loads a profile group by its exact display name (e.g. "Career Interests
// & Personality"), along with its facets and profiles. Profiles come back
// with `centroids` keyed by facet_id (same shape as the admin API returns).
// Returns null if no matching, active group exists yet — callers should
// treat that as "no profile set built out for this facet group yet" and
// skip it, not as an error.
//
// Caching is layered and optional, so this works with zero setup:
//   1. In-memory (this module) — always on, cleared on page reload.
//   2. Persistent — pass `cacheGet`/`cacheSet` to survive reloads (e.g.
//      backed by Dexie in dashboardDb.js). cacheGet(groupName) should
//      resolve to `{ data, timestamp } | null`; cacheSet(groupName, data)
//      should persist it.
// Default ttlMs is 24h — generous since this is admin-managed data that
// rarely changes, but short enough that admin edits show up within a day
// without a manual cache-bust.
export async function loadProfileGroupByName(groupName, headers, options = {}) {
  const { cacheGet, cacheSet, ttlMs = 60 * 24 * 60 * 60 * 1000 } = options;

  if (sessionCache.has(groupName)) return sessionCache.get(groupName);

  const promise = (async () => {
    if (cacheGet) {
      try {
        const cached = await cacheGet(groupName);
        if (cached && Date.now() - cached.timestamp < ttlMs) {
          return cached.data;
        }
      } catch (err) {
        console.warn(`Could not read profile group cache for "${groupName}":`, err);
      }
    }

    const fresh = await fetchProfileGroupFromNetwork(groupName, headers);

    if (cacheSet) {
      try {
        // Cache misses (fresh === null, i.e. "no group with this name yet")
        // are cached too — otherwise every load re-fetches the full groups
        // list just to confirm the same group still doesn't exist.
        await cacheSet(groupName, fresh);
      } catch (err) {
        console.warn(`Could not persist profile group cache for "${groupName}":`, err);
      }
    }

    return fresh;
  })();

  sessionCache.set(groupName, promise);
  return promise;
}

// Clears only the in-memory layer (e.g. after an admin edits profiles in
// another tab and you want this session to pick it up immediately, ahead
// of the persistent cache's TTL). The persistent cache is untouched here —
// bust that separately (e.g. bustProfileGroupsCache() in dashboardDb.js)
// if you need both cleared.
export function bustProfileMatchingSessionCache() {
  sessionCache.clear();
}

// radarLabels / radarScores are exactly what the existing radar chart
// already computes (chart.labels, chart.datasets[0].data) — the facet
// names as actually scored (short forms: "Cognitive", "Realistic", etc.)
// and their 1-5 averages, index-aligned.
//
// Facets are matched to radar labels via `short_label`, since that's the
// field that matches the raw facet names used when scoring — the long
// `label` ("Cognitive Strengths") is display-only.
export function computeProfileMatches(radarLabels, radarScores, facets, profiles) {
  if (!facets?.length || !profiles?.length) return null;

  const scoreByFacetId = {};
  facets.forEach((f) => {
    const idx = radarLabels.findIndex(
      (l) => l.trim().toLowerCase() === f.short_label.trim().toLowerCase()
    );
    if (idx !== -1) scoreByFacetId[f.id] = radarScores[idx];
  });

  // If the student's scores don't cover every facet this group needs,
  // matching would be unfairly skewed toward whichever facets ARE present —
  // better to say "not available" than show a misleading match.
  const missingFacet = facets.some((f) => scoreByFacetId[f.id] === undefined);
  if (missingFacet) return null;

  const scored = profiles
    .map((profile) => {
      let sumSq = 0;
      let countedFacets = 0;
      facets.forEach((f) => {
        const centroid = profile.centroids[f.id];
        if (centroid === undefined) return; // profile missing a centroid for this facet
        sumSq += (scoreByFacetId[f.id] - centroid) ** 2;
        countedFacets += 1;
      });
      return countedFacets > 0 ? { profile, distance: Math.sqrt(sumSq) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.distance - b.distance);

  if (scored.length === 0) return null;

  // Max possible distance on a 1-5 scale across N facets — turns the raw
  // Euclidean distance into an intuitive 0-100% "fit" for display.
  const maxPossibleDistance = Math.sqrt(facets.length * 4 ** 2);
  const toFitPercent = (distance) =>
    Math.max(0, Math.round(100 - (distance / maxPossibleDistance) * 100));

  return {
    best: { ...scored[0], fitPercent: toFitPercent(scored[0].distance) },
    runnerUp: scored[1] ? { ...scored[1], fitPercent: toFitPercent(scored[1].distance) } : null,
    all: scored,
  };
}